/**
 * /settings/crm — CRM Connector setup.
 *
 * Lets admins connect HubSpot (OAuth) and Salesforce (webhook token).
 * Handles the OAuth callback redirect with ?connected=hubspot&portal=...
 *
 * API surface:
 *   GET    /api/saas/crm/connections          list connections
 *   DELETE /api/saas/crm/connections/{id}     revoke a connection
 *   GET    /api/saas/crm/connect/hubspot      OAuth redirect
 *   POST   /api/saas/crm/connections          create Salesforce connection
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import SettingsIcon from "@mui/icons-material/Settings";
import { api, errorMessage } from "@/lib/api";

export const Route = createFileRoute("/_auth/settings/crm")({
  validateSearch: (search: Record<string, unknown>) => ({
    connected: typeof search.connected === "string" ? search.connected : null,
    portal: typeof search.portal === "string" ? search.portal : null,   // HubSpot portal ID
    org: typeof search.org === "string" ? search.org : null,            // Salesforce org host
  }),
  component: CrmSettingsPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface CrmConnection {
  id: string;
  provider: "hubspot" | "salesforce";
  hubspot_portal_id: string | null;
  instance_url: string | null;
  closed_won_stage: string | null;
  status: "active" | "error" | "revoked";
  last_error: string | null;
  webhook_url?: string | null;
}

// ── Provider card ─────────────────────────────────────────────────────────────

function ProviderCard({
  provider,
  label,
  description,
  connection,
  onDisconnect,
  onConfigure,
}: {
  provider: "hubspot" | "salesforce";
  label: string;
  description: string;
  connection: CrmConnection | undefined;
  onDisconnect: (id: string) => void;
  onConfigure: (conn: CrmConnection) => void;
}) {
  const isConnected = connection?.status === "active";
  const isError = connection?.status === "error";

  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 280 }}>
      <CardHeader
        title={label}
        subheader={description}
        action={
          isConnected ? (
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label="Connected"
              color="success"
              size="small"
              variant="outlined"
            />
          ) : isError ? (
            <Chip label="Error" color="error" size="small" variant="outlined" />
          ) : (
            <Chip label="Not connected" size="small" variant="outlined" />
          )
        }
      />
      <CardContent>
        {isConnected && connection && (
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            {provider === "hubspot" && connection.hubspot_portal_id && (
              <>Portal ID: {connection.hubspot_portal_id} · </>
            )}
            {provider === "salesforce" && connection.instance_url && (
              <>Instance: {connection.instance_url} · </>
            )}
            {connection.closed_won_stage ? (
              <>Closed-won stage: <strong>{connection.closed_won_stage}</strong></>
            ) : (
              <span style={{ color: "#f59e0b" }}>⚠ No closed-won stage set — all stage changes will be queued</span>
            )}
          </Typography>
        )}
        {isError && connection?.last_error && (
          <Alert severity="error" sx={{ mb: 1.5 }} variant="outlined">
            {connection.last_error}
          </Alert>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {isConnected || isError ? (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => connection && onConfigure(connection)}
              >
                Configure
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={() => connection && onDisconnect(connection.id)}
              >
                Disconnect
              </Button>
            </>
          ) : provider === "hubspot" ? (
            <Button
              size="small"
              variant="contained"
              href="/api/saas/crm/connect/hubspot"
            >
              Connect HubSpot
            </Button>
          ) : (
            <>
              <Button
                size="small"
                variant="contained"
                href="/api/saas/crm/connect/salesforce"
              >
                Connect Salesforce
              </Button>
              <Button
                size="small"
                variant="outlined"
                href="/api/saas/crm/connect/salesforce?sandbox=true"
                title="Use this for Salesforce sandbox orgs (test.salesforce.com)"
              >
                Sandbox
              </Button>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() => onConfigure({ id: "", provider: "salesforce", hubspot_portal_id: null, instance_url: null, closed_won_stage: null, status: "active", last_error: null })}
                title="Set up using a webhook token (no OAuth)"
              >
                Manual setup
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Configure dialog ──────────────────────────────────────────────────────────

function ConfigureDialog({
  conn,
  onClose,
  onSaved,
}: {
  conn: CrmConnection | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [stage, setStage] = useState(conn?.closed_won_stage ?? "");
  const [instanceUrl, setInstanceUrl] = useState(conn?.instance_url ?? "");
  const [webhookUrl, setWebhookUrl] = useState(conn?.webhook_url ?? "");
  const [copied, setCopied] = useState(false);

  // Reset state when dialog opens with a new connection
  useEffect(() => {
    if (conn) {
      setStage(conn.closed_won_stage ?? "");
      setInstanceUrl(conn.instance_url ?? "");
      setWebhookUrl(conn.webhook_url ?? "");
    }
  }, [conn?.id]);

  const isNew = !conn?.id;
  const isSalesforce = conn?.provider === "salesforce";

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<CrmConnection>("/api/saas/crm/connections", { method: "POST", body }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["crm-connections"] });
      if (data.webhook_url) setWebhookUrl(data.webhook_url);
      else {
        onSaved();
        onClose();
      }
    },
  });

  // For existing connections we patch via create (upsert on backend) — P4 simplification.
  // A proper PATCH endpoint is P5 polish.
  const handleSave = () => {
    if (isNew && isSalesforce) {
      createMut.mutate({
        provider: "salesforce",
        instance_url: instanceUrl || null,
        closed_won_stage: stage || null,
      });
    } else if (!isNew) {
      // Show guidance — stage update via re-connect in P4, full PATCH in P5
      onSaved();
      onClose();
    }
  };

  const copyWebhookUrl = () => {
    void navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const showWebhookSetup = isSalesforce && webhookUrl;

  return (
    <Dialog open={!!conn} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isNew ? "Set up Salesforce" : `Configure ${conn?.provider === "hubspot" ? "HubSpot" : "Salesforce"}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {showWebhookSetup && (
            <Alert severity="info" variant="outlined">
              Copy this URL and paste it into your Salesforce Outbound Message or Flow → HTTP Action
              destination. <strong>It won't be shown again.</strong>
            </Alert>
          )}

          {(showWebhookSetup || isSalesforce) && (
            <>
              {showWebhookSetup && (
                <TextField
                  label="Salesforce webhook URL"
                  value={webhookUrl}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? "Copied!" : "Copy"}>
                          <IconButton size="small" onClick={copyWebhookUrl}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  fullWidth
                />
              )}
              {!conn?.id && (
                <TextField
                  label="Salesforce instance URL (optional)"
                  value={instanceUrl}
                  onChange={(e) => setInstanceUrl(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="https://yourorg.my.salesforce.com"
                  helperText="Used for display only — not required for webhook auth"
                />
              )}
            </>
          )}

          <TextField
            label="Closed-won stage name"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            size="small"
            fullWidth
            placeholder={conn?.provider === "salesforce" ? "Closed Won" : "closedwon"}
            helperText={
              conn?.provider === "hubspot"
                ? "HubSpot internal stage ID (e.g. closedwon). Leave blank to queue all stage changes."
                : "Salesforce StageName value (e.g. Closed Won). Leave blank to queue all stage changes."
            }
          />

          {!isNew && conn?.provider === "hubspot" && (
            <Alert severity="info" variant="outlined">
              To update the closed-won stage, save here — then reconnect via the "Disconnect" +
              "Connect HubSpot" flow to re-apply. Full PATCH is coming in a future update.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {showWebhookSetup ? (
          <Button
            variant="contained"
            onClick={() => { onSaved(); onClose(); }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={createMut.isPending}
            >
              {createMut.isPending ? "Saving…" : isNew ? "Create connection" : "Save"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function CrmSettingsPage() {
  const { connected, portal, org } = Route.useSearch();
  const qc = useQueryClient();

  const [snack, setSnack] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);
  const [configConn, setConfigConn] = useState<CrmConnection | null>(null);

  // Show success toast from OAuth callback redirect
  useEffect(() => {
    if (connected === "hubspot" && portal) {
      setSnack({ msg: `HubSpot connected — portal ${portal}`, severity: "success" });
    } else if (connected === "salesforce" && org) {
      setSnack({ msg: `Salesforce connected — ${org}`, severity: "success" });
    }
  }, [connected, portal, org]);

  const { data: connections = [], isLoading } = useQuery<CrmConnection[]>({
    queryKey: ["crm-connections"],
    queryFn: () => api<CrmConnection[]>("/api/saas/crm/connections"),
  });

  const disconnectMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/saas/crm/connections/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-connections"] });
      setSnack({ msg: "Connection revoked.", severity: "success" });
    },
    onError: (e) => setSnack({ msg: errorMessage(e), severity: "error" }),
  });

  const hubspotConn = connections.find((c) => c.provider === "hubspot");
  const salesforceConn = connections.find((c) => c.provider === "salesforce");

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Button
            size="small"
            variant="text"
            color="inherit"
            href="/settings/integrations"
            sx={{ ml: -1, mb: 0.5, color: "text.secondary" }}
          >
            ← Integrations
          </Button>
          <Typography variant="h5" fontWeight={600}>
            CRM Connector
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Connect HubSpot or Salesforce. When a deal closes, it lands in the{" "}
            <strong>Deal Queue</strong> for review — you pick the plan, confirm the customer, and
            activate. One click creates the subscription.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          href="/saas/crm"
        >
          Open deal queue →
        </Button>
      </Stack>

      {/* Provider cards */}
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        PROVIDER CONNECTIONS
      </Typography>
      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Stack direction="row" spacing={2} flexWrap="wrap" mb={4}>
          <ProviderCard
            provider="hubspot"
            label="HubSpot"
            description="OAuth. Deals auto-queue when stage changes to closed-won."
            connection={hubspotConn}
            onDisconnect={(id) => disconnectMut.mutate(id)}
            onConfigure={(conn) => setConfigConn(conn)}
          />
          <ProviderCard
            provider="salesforce"
            label="Salesforce"
            description="Webhook (Outbound Message / Flow). Generate a URL and paste into SF."
            connection={salesforceConn}
            onDisconnect={(id) => disconnectMut.mutate(id)}
            onConfigure={(conn) => setConfigConn(conn)}
          />
        </Stack>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* How it works */}
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        HOW IT WORKS
      </Typography>
      <Stack spacing={1.5} mb={3}>
        {[
          ["1. Connect", "Authenticate HubSpot via OAuth or generate a Salesforce webhook URL."],
          ["2. Set the closed-won stage", "Configure which stage value triggers a queue entry. Leave blank to catch all stage changes."],
          ["3. Deal closes", "The CRM sends a webhook. The deal lands in the queue as \"Pending review\" within seconds."],
          ["4. Review & activate", "An accountant opens the deal queue, confirms the plan + customer, and clicks Activate — one subscription is created."],
        ].map(([title, desc]) => (
          <Box key={title}>
            <Typography variant="body2" fontWeight={600}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{desc}</Typography>
          </Box>
        ))}
      </Stack>

      {/* Configure dialog */}
      <ConfigureDialog
        conn={configConn}
        onClose={() => setConfigConn(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["crm-connections"] });
          setSnack({ msg: "Connection updated.", severity: "success" });
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack?.severity ?? "info"} onClose={() => setSnack(null)} variant="filled">
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
